import { useCallback, useState, useMemo } from 'react'
import _ from 'lodash'
import { newAppliance } from '../../../common/appliance'
import { copyAppliance } from './utils'

export default ({ field, model, appliances, defaultLocale }) => {

  const [currentTab, setCurrentTab] = useState('ingredients')
  const [showAddApliance, setShowAddApliance] = useState(false)

  const [selectedStep, selectStep] = useState(null)
  const [selectMapInfo, setSelectMapInfo] = useState({ map: {} })
  const [selectedIngredient, selectIngredient ] = useState(null)

  const stepsSelector = useMemo(() => ({
    selectedStep,
    selectStep,
    selectMapInfo,
    setSelectMapInfo,
  }), [selectMapInfo, selectedStep])

  const ingredientsSelector = useMemo(() => ({
    selectedIngredient,
    selectIngredient,
  }), [selectedIngredient])

  const { isValidPath, onChange } = model
  const defaultAppliance = appliances[0]

  const freeAppliances = useMemo(() => {
    const usedAppliances = Reflect.ownKeys(_.get(model, `values[${field}].applianceSteps`, {
      [defaultAppliance.id]: [],
    })).map(id => parseInt(id, 10))
    const availableAppliances = appliances.filter(appliance => !usedAppliances.includes(appliance.id))
    return availableAppliances
  },[appliances, field, model, defaultAppliance])

  const tabsInfo = useMemo( () => {
    const currentSteps = _.get(model, `values[${field}].applianceSteps`, {
      [defaultAppliance.id]: [],
    })
    const steps = Reflect.ownKeys(currentSteps).map(applianceId => {
      const appliance = appliances.find(a => a.id === parseInt(applianceId, 10))
      return ({
        value: `steps,${applianceId}`,
        label: `Steps '${appliance.description}'`,
        error: !isValidPath(`values[${field}].preparationSteps`) ||
          !isValidPath(`values[${field}].applianceSteps.${appliance.id}`),
      })
    })
    return {
      tabs: [{
        value: 'ingredients',
        label: 'Ingredients',
        error: !isValidPath(`values[${field}].sections`),
      }, ...steps],
      stepsTabs: steps,
    }
  }, [appliances, defaultAppliance.id, field, isValidPath, model])

  const handleAdd = useCallback(({ appliance, applianceToCopy }) => {
    onChange({
      target: {
        value: {
          ..._.get(model, `values[${field}].applianceSteps`, {}),
          [appliance.id]: applianceToCopy ? copyAppliance(applianceToCopy) : newAppliance(defaultLocale),
        },
        name: `values[${field}].applianceSteps`,
      },
    })
    setShowAddApliance(false)
    setCurrentTab(`steps,${appliance.id}`)
  }, [field, model, onChange, defaultLocale])

  const currentAppliance = useMemo(() => {
    if(currentTab.includes('steps')) {
      const applianceId = parseInt(currentTab.split(',')[1], 10)
      const appliance = appliances.find(a => a.id === parseInt(applianceId))
      return appliance
    }
    return null
  }, [appliances, currentTab])

  const handleChangeAppliance = useCallback((({
    newAppliance,
    oldAppliance,
  }) => {
    let oldPath = `values[${field}].applianceSteps.${oldAppliance}`
    let newPath = `values[${field}].applianceSteps.${newAppliance}`
    const dataToMove = _.get(model, oldPath, [])
    setCurrentTab(`steps,${newAppliance}`)
    setTimeout(() => {
      if (model.values[field].applianceSteps) {
        delete model.values[field].applianceSteps[oldAppliance]
      }
      onChange({
        target: {
          value: {
            ...(model.values[field].applianceSteps || {}),
            [newAppliance]: dataToMove,
          },
          name: `values[${field}].applianceSteps`,
        },
      }, (model, modifyUtils) => modifyUtils.changeErrorsPath(model, oldPath, newPath))
    }, 10)
  }), [field, model, onChange])

  const [itemToDelete, setShowDeleteModal] = useState(null)

  const handleDelete = useCallback(step => {
    setShowDeleteModal({
      applianceId: parseInt(step.split(',')[1], 10),
    })
  }, [])

  const onDelete = useCallback(() => {
    const { applianceId } = itemToDelete
    const toSelect = Reflect.ownKeys(model.values[field].applianceSteps).filter(
      app => app !== `${applianceId}`,
    )[0]
    setCurrentTab(`steps,${toSelect}`)

    setTimeout(() => {
      delete model.values[field].applianceSteps[applianceId]
      onChange({
        target: {
          name: `values[${field}].applianceSteps`,
          value: model.values[field].applianceSteps,
        },
      },
      (model, modifyUtils) => modifyUtils.removeErrorsBlock(
        model, `values[${field}].applianceSteps.${applianceId}`,
      ))
      setShowDeleteModal(false)
    },10)
  }, [field, itemToDelete, model.values, onChange])

  return {
    currentTab, setCurrentTab,
    freeAppliances,
    tabsInfo,
    handleAdd,
    currentAppliance,
    handleChangeAppliance,
    handleDelete,
    onDelete,
    setShowDeleteModal,
    itemToDelete,
    showAddApliance, setShowAddApliance,
    stepsSelector,
    ingredientsSelector,
  }
}
